import { createContext, useContext, useRef } from 'react';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';

/**
 * One Save per page (spec "Saving and unsaved changes"): the cards on a page hand their Save to the
 * page's sticky footer. The pieces:
 * - FooterSaveProvider (FooterSaveProvider.tsx) holds the cards' registrations for one page or tab.
 * - useFooterSave, here: a card registers its state and its save and discard functions.
 * - useSaveHold, here: a card hands the page a save it runs by its own button, so leaving the tab or
 *   the page waits for that save's answer.
 * - StickySaveFooter (StickySaveFooter.tsx) shows the status and the one Save.
 * - useFooterSaveState, here: what the page reads to ask before leaving (dirty, saveAll, discardAll,
 *   held, settleHeld).
 */

/** What a card hands the footer. */
export interface FooterSaveCard {
  /** Unique within the provider. Saves run by `order`, then in the order the cards first registered. */
  id: string;
  /**
   * Where this card's save runs among the page's: lower first; cards with the same order (by default
   * all of them, 0) keep the order they first registered in. For a page whose saves must run in a set
   * order whatever React mounts first: a child's effect runs before its parent's, so a card nested in
   * another would otherwise register, and save, first (the Workflow page's approver matrix sits inside
   * its Approvals section, and saves after it).
   */
  order?: number;
  /** The card holds a change that is not saved yet. */
  dirty: boolean;
  /** False while the change cannot be saved as it stands (a number out of range). Default true. */
  canSave?: boolean;
  /** The card is saving by another way (one of its own buttons). Default false. */
  saving?: boolean;
  /**
   * Saves the card's change and resolves to whether it worked. The card shows its own error when it
   * did not, as it always has; never reject (a rejection counts as a failure all the same).
   */
  save: () => Promise<boolean>;
  /** Puts the card back to its last saved values. */
  discard: () => void;
}

/** A card as the provider holds it: every field present, the functions calling the card's latest. */
export interface FooterSaveEntry {
  id: string;
  order: number;
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  save: () => Promise<boolean>;
  discard: () => void;
}

export interface FooterSaveRegistry {
  set: (entry: FooterSaveEntry) => void;
  remove: (id: string) => void;
}

/** The footer's status line: "No unsaved changes", "Unsaved changes on this page", "All changes saved". */
export type FooterSaveStatus = 'clean' | 'dirty' | 'saved';

export interface FooterSaveState {
  /** Some registered card holds a change that is not saved. */
  dirty: boolean;
  /** The footer's Save would run now: something is dirty, every dirty card can save, and nothing is saving. */
  canSave: boolean;
  /** The footer's save (saveAll) is running. */
  saving: boolean;
  status: FooterSaveStatus;
  /**
   * Saves every dirty card by `order` (lower first, ties in registration order), one at a time, and
   * resolves to whether they ALL worked; it carries on past a failure, so each card that can save
   * does. Shows the "Changes saved" toast only when all worked. Resolves true at once when nothing is
   * dirty, and false at once when a dirty card cannot save or a card is saving. A second call while
   * one runs gets the first's answer.
   */
  saveAll: () => Promise<boolean>;
  /** Asks every dirty card to put back its last saved values. */
  discardAll: () => void;
  /** A save handed to useSaveHold is running, on any tab, with the footer or without it. */
  held: boolean;
  /**
   * Resolves once no save handed to useSaveHold is running (at once when none is), to whether every
   * one that was still running when it was called, or started while it waited, worked.
   */
  settleHeld: () => Promise<boolean>;
}

/** What useSaveHold hands a card: give it the save's request, and it gives the same promise back. */
export type SaveHold = <T>(run: Promise<T>) => Promise<T>;

/** Null outside a provider, and inside an inactive one (a tab with no footer). */
export const FooterSaveRegistryContext = createContext<FooterSaveRegistry | null>(null);
/** Null outside a provider. */
export const FooterSaveStateContext = createContext<FooterSaveState | null>(null);
/** Null outside a provider. Provided by an inactive one too: a tab without the footer holds its saves. */
export const SaveHoldContext = createContext<SaveHold | null>(null);

/** useSaveHold outside a provider: holds nothing, and still leaves no rejection unhandled. */
const passThrough: SaveHold = (run) => {
  run.catch(() => undefined);
  return run;
};

/**
 * Registers a card with the page's sticky Save footer, if it is inside an active FooterSaveProvider.
 * Returns `{ inFooter }`:
 * - true: the footer saves this card. The card hides its own Save button (and anything else that only
 *   starts a save) and keeps everything else, its error and "Saved" lines included.
 * - false: no footer here (no provider, or a tab without a footer): the hook does nothing, and the card
 *   renders exactly as it always has, its own Save included. Cards rendered on other pages and in their
 *   own specs take this path.
 *
 * Call it on every render with the card's current values. `save` and `discard` may be new functions on
 * every render: the footer always calls the latest ones.
 */
export function useFooterSave(card: FooterSaveCard): { inFooter: boolean } {
  const registry = useContext(FooterSaveRegistryContext);
  const latest = useRef(card);
  useIsoLayoutEffect(() => {
    latest.current = card;
  });

  const { id, dirty } = card;
  const order = card.order ?? 0;
  const canSave = card.canSave ?? true;
  const saving = card.saving ?? false;
  useIsoLayoutEffect(() => {
    if (!registry) return;
    registry.set({
      id,
      order,
      dirty,
      canSave,
      saving,
      save: () => latest.current.save(),
      discard: () => latest.current.discard(),
    });
  }, [registry, id, order, dirty, canSave, saving]);
  // Apart from the one above, so a change of state updates the entry in place (it keeps its place in
  // the order) and only leaving, or a new id, takes it out.
  useIsoLayoutEffect(() => {
    if (!registry) return;
    return () => registry.remove(id);
  }, [registry, id]);

  return { inFooter: registry !== null };
}

/**
 * For a save a card runs by its own button: a row form's Save, a Remove, Add member, a role change,
 * Disconnect. Hand the page the request as it starts, `void hold(save.mutateAsync(dto))`: on a
 * settings page (SettingsTabs), a tab switch or a route change asked for while it runs waits for its
 * answer, then goes only if the save worked; a save that failed keeps the person where the card shows
 * why. Without this the switch would unmount the card mid-request, and the answer, a failure above
 * all, would come back to a card that is gone and be shown nowhere.
 *
 * A request that rejects counts as a failed save, one that resolves as a save that worked. Returns
 * the promise it was given, so the card can still act on the outcome (`hold(run).then(...)`); the
 * hold handles a rejection itself, so `void hold(run)` leaves none unhandled (the card shows its
 * error from the mutation's own onError, as before). Outside a FooterSaveProvider (a card drawn on
 * another page, or in its own spec) it holds nothing and hands the promise straight back.
 */
export function useSaveHold(): SaveHold {
  return useContext(SaveHoldContext) ?? passThrough;
}

/** The page's view of its footer (see FooterSaveState); null outside a FooterSaveProvider. */
export function useFooterSaveState(): FooterSaveState | null {
  return useContext(FooterSaveStateContext);
}
